// Admin-Panel › Server: Suche, Filter „gespiegelt", umbenennen, Besitzer übertragen, löschen.
import type { AdminUserDto } from '@lagerfeuer/shared';
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { adminApi, type AdminServer } from '../api';
import { displayName, formatDateTime, formatNumber } from '../format';
import { toast } from '../toast';
import { Badge, Button, ConfirmButton, Dialog, Empty, ErrorBox, Field, Loading } from '../ui';
import { useDebounced } from './hooks';
import { AdminError, AdminSection, UserPicker } from './parts';
import { ADMIN_SERVERS_KEY, ADMIN_STATS_KEY } from './realtime';

type MirroredFilter = 'all' | 'mirrored' | 'local';
const PAGE_SIZE = 50;

function RenameDialog(props: { server: AdminServer; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(props.server.name);
  const trimmed = name.trim();
  const invalid = trimmed.length < 1 || trimmed.length > FIXED_LIMITS.serverNameMax;
  const save = useMutation({
    mutationFn: () => adminApi.updateServer(props.server.id, { name: trimmed }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SERVERS_KEY });
      toast({ kind: 'success', title: 'Server umbenannt' });
      props.onClose();
    },
  });
  return (
    <Dialog
      title="Server umbenennen"
      testId="admin-server-rename-dialog"
      onClose={props.onClose}
      footer={
        <>
          <Button testId="admin-server-rename-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" testId="admin-server-rename-submit" disabled={invalid || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </Button>
        </>
      }
    >
      <Field label="Name" error={invalid ? `1–${FIXED_LIMITS.serverNameMax} Zeichen` : null}>
        {(id) => <input id={id} className="lfx-input" value={name} data-testid="admin-server-rename-input" onChange={(e) => setName(e.target.value)} />}
      </Field>
      <ErrorBox error={save.error} testId="admin-server-rename-error" />
    </Dialog>
  );
}

function TransferDialog(props: { server: AdminServer; onClose: () => void }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<AdminUserDto | null>(null);
  const save = useMutation({
    mutationFn: (ownerId: string) => adminApi.updateServer(props.server.id, { ownerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SERVERS_KEY });
      toast({ kind: 'success', title: 'Besitzer übertragen' });
      props.onClose();
    },
  });
  return (
    <Dialog
      title={`Besitz von „${props.server.name}" übertragen`}
      testId="admin-server-transfer-dialog"
      onClose={props.onClose}
      footer={
        <>
          <Button testId="admin-server-transfer-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" testId="admin-server-transfer-submit" disabled={!target || save.isPending} onClick={() => target && save.mutate(target.id)}>
            Übertragen
          </Button>
        </>
      }
    >
      <p className="lfx-muted">Aktueller Besitzer: {displayName(props.server.owner)}</p>
      <UserPicker testId="admin-server-transfer-picker" label="Neuer Besitzer" excludeId={props.server.ownerId} onSelect={setTarget} />
      {target && (
        <p data-testid="admin-server-transfer-selected">
          Ausgewählt: <strong>{displayName(target)}</strong> <span className="lfx-muted">@{target.username}</span>
        </p>
      )}
      <ErrorBox error={save.error} testId="admin-server-transfer-error" />
    </Dialog>
  );
}

function ServerRow(props: { server: AdminServer; onRename: () => void; onTransfer: () => void }) {
  const qc = useQueryClient();
  const s = props.server;
  const remove = useMutation({
    mutationFn: () => adminApi.deleteServer(s.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SERVERS_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_STATS_KEY });
      toast({ kind: 'success', title: 'Server gelöscht', body: s.name });
    },
  });
  return (
    <tr data-testid={`admin-server-row-${s.id}`}>
      <td>
        <strong>{s.name}</strong>{' '}
        {s.isMirrored && (
          <Badge tone="mirror" testId={`admin-server-mirrored-${s.id}`} title={s.mirrorSource ?? undefined}>
            gespiegelt
          </Badge>
        )}
        <div className="lfx-muted lfx-mono">{s.id}</div>
      </td>
      <td>
        <Link to={`../users/${s.owner.id}`} relative="path" className="lfx-link" data-testid={`admin-server-owner-${s.id}`}>
          {displayName(s.owner)}
        </Link>
      </td>
      <td>{formatNumber(s.memberCount)}</td>
      <td>{formatDateTime(s.createdAt)}</td>
      <td>
        <div className="lfx-row">
          <Button testId={`admin-server-rename-${s.id}`} onClick={props.onRename}>
            Umbenennen
          </Button>
          <Button testId={`admin-server-transfer-${s.id}`} onClick={props.onTransfer}>
            Besitzer
          </Button>
          <ConfirmButton
            label="Löschen"
            confirmLabel="Endgültig löschen?"
            variant="danger"
            testId={`admin-server-delete-${s.id}`}
            disabled={remove.isPending}
            onConfirm={() => remove.mutate()}
          />
        </div>
        {s.isMirrored && (
          <div className="lfx-field__hint" data-testid={`admin-server-mirror-hint-${s.id}`}>
            Gespiegelte Server besser unter <Link to="../bridge" relative="path" className="lfx-link">Brücke</Link> entfernen, sonst legt die Brücke ihn neu an.
          </div>
        )}
        <ErrorBox error={remove.error} testId={`admin-server-delete-error-${s.id}`} />
      </td>
    </tr>
  );
}

export function ServersPage() {
  const [q, setQ] = useState('');
  const [mirrored, setMirrored] = useState<MirroredFilter>('all');
  const [renaming, setRenaming] = useState<AdminServer | null>(null);
  const [transferring, setTransferring] = useState<AdminServer | null>(null);
  const closeRename = useCallback(() => setRenaming(null), []);
  const closeTransfer = useCallback(() => setTransferring(null), []);
  const debounced = useDebounced(q.trim(), 300);
  const list = useInfiniteQuery({
    queryKey: [...ADMIN_SERVERS_KEY, debounced, mirrored],
    queryFn: ({ pageParam }) =>
      adminApi.servers({
        q: debounced || undefined,
        mirrored: mirrored === 'all' ? undefined : mirrored === 'mirrored',
        after: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const servers = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <AdminSection title="Server" testId="admin-servers">
      <div className="lfx-stack">
        <div className="lfx-admin-filters">
          <Field label="Suche">
            {(id) => (
              <input id={id} type="search" className="lfx-input" value={q} placeholder="Servername" data-testid="admin-servers-search" onChange={(e) => setQ(e.target.value)} />
            )}
          </Field>
          <Field label="Herkunft">
            {(id) => (
              <select id={id} className="lfx-select" value={mirrored} data-testid="admin-servers-mirrored" onChange={(e) => setMirrored(e.target.value as MirroredFilter)}>
                <option value="all">Alle</option>
                <option value="mirrored">Nur gespiegelte</option>
                <option value="local">Nur eigene</option>
              </select>
            )}
          </Field>
        </div>
        {list.isLoading && <Loading testId="admin-servers-loading" />}
        <AdminError error={list.error} testId="admin-servers-error" />
        {list.isSuccess && servers.length === 0 && <Empty testId="admin-servers-empty">Keine Server gefunden.</Empty>}
        {servers.length > 0 && (
          <div className="lfx-table-wrap">
            <table className="lfx-table" data-testid="admin-servers-list">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Besitzer</th>
                  <th scope="col">Mitglieder</th>
                  <th scope="col">Erstellt</th>
                  <th scope="col">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {servers.map((s) => (
                  <ServerRow key={s.id} server={s} onRename={() => setRenaming(s)} onTransfer={() => setTransferring(s)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage && (
          <Button testId="admin-servers-more" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
          </Button>
        )}
      </div>
      {renaming && <RenameDialog server={renaming} onClose={closeRename} />}
      {transferring && <TransferDialog server={transferring} onClose={closeTransfer} />}
    </AdminSection>
  );
}
